import { useEffect, useState, useCallback } from "react";
import {
  Box,
  Button,
  Grid,
  Paper,
  Stack,
  TextField,
  Typography,
} from "@mui/material";
import { AdapterDayjs } from "@mui/x-date-pickers/AdapterDayjs";
import {
  LocalizationProvider,
  DatePicker,
  TimePicker,
} from "@mui/x-date-pickers";
import { INT16, PUT, POST, WAV } from "../utils/constants";
import dayjs from "dayjs";
import "../../AppPage.css";
import { pluralize } from "../utils/stringHelpers";
import { useNavigate, useParams, useLocation } from "react-router-dom";
import { ContentTitleBar } from "../../../components/ContentTitleBar";
import FileDropzone from "../../../components/upload/FileDropzone";
import { ChevronRight } from "@mui/icons-material";
import { FileRenderer } from "./FileRenderer";
import { ImportErrorSnackbar } from "../../../components/snackbars";
import { exists } from "../../../store/validation";
import {
  ClipImportWarningDialog,
  WaitDialog,
} from "../../../components/dialogs";
import { SERVER_URL } from "../../../store/config";
import { loadClip } from "../utils/dataLoader";
import { canEditCase } from "../../../store/AclUtil";
import { useAuth } from "../../../store/auth/hooks";
import { useCase } from "../../../store/case/hooks";
import { useModals } from "../../../store/dialogs/hooks";
import { useSnackbars } from "../../../store/snackbars/hooks";
import { useUploadQueue } from "../../../store/uploadQueue/hooks";
import { Controller, FormProvider, useForm } from "react-hook-form";
import z from "zod";
import { zodResolver } from "@hookform/resolvers/zod";

const AddClipSchema = () =>
  z
    .object({
      description: z
        .string()
        .trim()
        .nonempty({ message: "A clip name is required." }),
      location: z.string().trim().optional(),
      recordingTime: z.coerce.date().nullable().optional(),
      fileType: z.string().trim().nonempty(),
      display: z.string().trim().nonempty(),
      sampleRate: z.preprocess(
        (val) => (val === "" ? undefined : val),
        z.coerce
          .number({ message: "Please enter a valid number." })
          .min(0, "Must be at least 0."),
      ),
      numberOfChannels: z.number().optional(),
      recordingDevice: z.number().nullable(),
      // sync: z.boolean().nonoptional(),
      arrayWidth: z.preprocess(
        (val) => (val === "" ? undefined : val),
        z.coerce
          .number({ message: "Please enter a valid number." })
          .int("Must be an integer.")
          .min(0, "Must be at least 0."),
      ),
      comment: z.string().trim().optional(),
    })
    .superRefine((data, ctx) => {
      const isMonoWav = data.numberOfChannels === 1 && data.fileType === WAV;
      const isMultiChannelWav =
        data.fileType === WAV && data.numberOfChannels > 1;
      const isInt16 = data.fileType === INT16;

      if (!isMonoWav && !isMultiChannelWav && !isInt16 && data.arrayWidth < 0) {
        ctx.addIssue({
          code: z.ZodIssueCode.custom,
          path: ["arrayWidth"],
          message: "Must be at least 0.",
        });
      }

      if (isInt16) {
        if (data.numberOfChannels === 1) {
          ctx.addIssue({
            code: z.ZodIssueCode.custom,
            path: ["numberOfChannels"],
            message: "Number of channels is required.",
          });
        }
      }
    });

export default function AddClipPage() {
  const navigate = useNavigate();
  const { case: af_case, isLoading, loadCase } = useCase();
  const {
    reset: uploadQueueReset,
    removeFile: uploadQueueRemoveFile,
    updateFileData: uploadQueueUpdateFileData,
    addFiles: uploadQueueAddFiles,
    canStartUpload,
    isUploading,
    files,
    fileData,
  } = useUploadQueue();
  const { closeClipImportWarningDialog, openClipImportWarningDialog } =
    useModals();
  const { openImportErrorSnackbar } = useSnackbars();
  const [warningMessage, setWarningMessage] = useState("");
  const [clip, setClip] = useState({});
  const { pathname } = useLocation();
  const [readOnly, setReadOnly] = useState(true);
  const { caseId, clipId } = useParams();
  const doesExist = clipId ? true : false;
  const { accessCode, user, logout, refreshToken } = useAuth();

  const methods = useForm({
    resolver: zodResolver(AddClipSchema()),
    values:
      {
        description: clip.description ?? "",
        location: clip.location ?? "",
        recordingTime: clip.recordingTime ?? null,
        recordingDevice: 1,
        // sync: false,
        arrayWidth: 0,
        comment: clip.comment ?? "",
        fileType: "",
        sampleRate: 0,
        numberOfChannels: 0,
        display: "unknown",
      } || {},
    mode: "all",
  });

  const {
    control,
    formState: { dirtyFields, errors, isValid },
    getValues,
    handleSubmit: rhfHandleSubmit,
    reset: rhfReset,
    resetField,
    setValue,
  } = methods;

  const disableImport = !isValid || readOnly || !canStartUpload;

  // Track previous file type (if any) to determine when file data fields should be reset
  // to prevent stale data from previous updates
  const [prevFileType, setPrevFileType] = useState();

  useEffect(() => {
    const { fileType, sampleRate, numberOfChannels, display } = fileData;
    const fileDataFields = { fileType, sampleRate, numberOfChannels, display };
    const RHF_OPTIONS = {
      shouldValidate: true,
      shouldDirty: false,
      shouldTouch: false,
    };
    const editableFields = [
      "arrayWidth",
      "numberOfChannels",
      "recordingDevice",
      "sampleRate",
      // "sync",
    ];

    // Check if any editable fields are dirty before resetting to prevent unnecessary resets
    const isEditableDirty = editableFields.some((field) => dirtyFields[field]);

    // Sets value for some import details fields when fileData changes (without clearing the entire form)
    Object.entries(fileDataFields).forEach(([field, value]) => {
      setValue(field, value, RHF_OPTIONS);
    });

    // Resets fields if all file data is removed to prevent stale data
    // with check to only run if fileType was previously set and is now cleared
    // (indicating file data was removed) to prevent unnecessary resets
    if (prevFileType && !fileType && isEditableDirty) {
      editableFields.forEach((field) => resetField(field));
    }

    setPrevFileType(fileType);
  }, [fileData, setValue, resetField, prevFileType, dirtyFields]);

  const resetValues = () => {
    rhfReset();
    setClip({});
  };

  const handleReset = () => {
    resetValues();
    navigate(`/app/cases/${caseId}/clips`, { replace: true });
  };

  const load = useCallback(async () => {
    if (isUploading) {
      console.log("Upload in progress, not loading");
      navigate(`/app/cases/${caseId}/clips`, { replace: true });
      return;
    }
    uploadQueueReset();
    loadCase(caseId);

    if (!clipId) return;

    const existingClip = await loadClip(caseId, clipId);

    if (existingClip) {
      const {
        description,
        location,
        recordingTime,
        fileType,
        numberOfChannels,
        recordingDeviceId,
        sampleRate,
        // sync,
        arrayWidth,
        comment,
      } = existingClip;

      setClip({
        description,
        location,
        fileType,
        numberOfChannels,
        sampleRate,
        // sync,
        arrayWidth,
        comment,
        recordingDevice: recordingDeviceId,
        recordingTime: recordingTime ? dayjs(recordingTime) : null,
      });
    }
  }, [uploadQueueReset, clipId, caseId, loadCase, isUploading, navigate]);

  // Applies channel/sample rate metadata from a sidecar JSON file. Needs the
  // fileData produced by the audio files it accompanies, so it takes it as an
  // argument instead of reading the (possibly stale) value from state.
  const applyJSONFileData = (json, currentFileData) => {
    if (currentFileData.fileType !== INT16) return;
    if (!exists(json) || !exists(json.elements)) return;

    const numberOfChannels = json.elements.length;
    if (!numberOfChannels) return;

    uploadQueueUpdateFileData({
      sampleRate: json.samplerate < 4000 ? undefined : json.samplerate,
      numberOfChannels: parseInt(numberOfChannels),
    });
  };

  // JSON files are processed last: they only update fields that the audio files
  // establish, so applying one before its .int16 lands leaves the channel count
  // and other import details unset.
  const addFiles = async (fileList, jsonData = []) => {
    let currentFileData = fileData;

    if (fileList.length > 0) {
      const { payload } = await uploadQueueAddFiles(fileList);
      console.log("[addFiles] Result: ", payload);
      if (!payload.success) {
        openImportErrorSnackbar(payload?.errors);
        return;
      }
      currentFileData = payload.fileData;
    }

    jsonData.forEach((json) => applyJSONFileData(json, currentFileData));
  };

  const manageClip = async (method) => {
    const formData = getValues();
    const { fileType, sampleRate, numberOfChannels } = formData;

    if (fileType === INT16) {
      uploadQueueUpdateFileData({
        sampleRate,
        numberOfChannels,
      });
    }

    const isPut = method === PUT;
    const url = isPut
      ? `${SERVER_URL}/api/cases/${caseId}/clips/${clipId}/upload`
      : `${SERVER_URL}/api/cases/${caseId}/clips`;

    const body = JSON.stringify({
      ...formData,
      recordingTime:
        method === POST && formData.recordingTime
          ? formData.recordingTime.toISOString()
          : formData.recordingTime,
    });

    try {
      const response = await fetch(url, {
        method: method,
        headers: {
          Accept: "application/json",
          "Content-Type": "application/json",
          Authorization: `Bearer ${accessCode}`,
        },
        body: body,
      });

      if (response.status === 200) {
        resetValues();
        const clip = await response.json();
        return clip;
      } else if ([401, 403].includes(response.status)) {
        logout();
        return null;
      } else {
        openImportErrorSnackbar([
          `Server returned error code: ${response.status}`,
        ]);
        console.error(
          `[manageClip] Server Returned error code: ${response.status}`,
        );
        return null;
      }
    } catch (e) {
      openImportErrorSnackbar([`Server exception: ${e.message}`]);
      console.error(`[manageClip] Server exception:`, e);
      return null;
    }
  };

  const submitClip = async () => {
    console.log("[addClip] Submitting New Case Clip");
    await refreshToken();
    return await manageClip(doesExist ? PUT : POST);
  };

  const handleSubmit = async () => {
    closeClipImportWarningDialog();
    const clip = await submitClip();
    if (clip) {
      navigate(`/app/cases/${caseId}/clips/${clip.id}/import`, {
        replace: true,
      });
    } else {
      // error!
    }
  };

  const handleImport = () => {
    const { foundChannels, numberOfChannels, fileType } = fileData;
    const isNamingInvalid = foundChannels === 0 && fileType !== INT16;
    const isChannelCountMismatch =
      foundChannels > 0 && foundChannels !== numberOfChannels;

    if (isNamingInvalid || isChannelCountMismatch) {
      const message = isNamingInvalid
        ? `File${pluralize(numberOfChannels, "s")} do${pluralize(numberOfChannels, "", "es")} not adhere to numerical naming convention. Continue to import the audio with channel${pluralize(numberOfChannels, "s")} named as shown or cancel and rename the file${pluralize(numberOfChannels, "s")}.`
        : `Found ${numberOfChannels} channel${pluralize(numberOfChannels, "s")} but expected ${foundChannels} based on file name${pluralize(numberOfChannels, "s")}. Continue to import the audio with channel${pluralize(numberOfChannels, "s")} named as shown or cancel to add the rest of the files.`;

      setWarningMessage(message);
      openClipImportWarningDialog();
      return;
    }

    handleSubmit();
  };

  useEffect(() => {
    load();
  }, [load]);

  useEffect(() => {
    console.log("Use effect for case and user", af_case, user);
    if (af_case.id !== undefined) {
      setReadOnly(!canEditCase(user, af_case));
      console.log("Set read only to ", !canEditCase(user, af_case));
    }
  }, [af_case, user]);

  useEffect(() => {
    if (isUploading) return;
    uploadQueueReset();
  }, [pathname, uploadQueueReset, isUploading]);

  return (
    <Stack component="main" height={1}>
      <FormProvider {...methods}>
        <Box
          component="form"
          noValidate
          onSubmit={rhfHandleSubmit(handleImport)}
        >
          <WaitDialog open={isLoading} />
          <Stack
            maxHeight="calc(100vh - 184px)"
            flex={1}
            p={3}
            sx={{ overflowY: "scroll" }}
          >
            <Typography variant="h6">Clip Details</Typography>
            <Grid container spacing={2} mt={2}>
              <Grid size={{ xs: 12, sm: 6 }}>
                <Controller
                  name="description"
                  control={control}
                  render={({ field }) => (
                    <TextField
                      {...field}
                      disabled={readOnly}
                      error={!!errors.description}
                      fullWidth
                      helperText={
                        errors.description
                          ? errors.description?.message
                          : "Please enter a name for the clip."
                      }
                      label="Clip Name"
                      required
                      slotProps={{ input: { readOnly: readOnly } }}
                    />
                  )}
                />
              </Grid>
              <Grid size={{ xs: 12, sm: 6, lg: 2.5, xl: 3 }}>
                <Controller
                  name="location"
                  control={control}
                  render={({ field }) => (
                    <TextField
                      {...field}
                      disabled={readOnly}
                      fullWidth
                      label="Location"
                      slotProps={{ input: { readOnly: readOnly } }}
                    />
                  )}
                />
              </Grid>
              <Grid size={{ xs: 12, sm: 6, md: 3, lg: 1.75, xl: 1.5 }}>
                <LocalizationProvider dateAdapter={AdapterDayjs}>
                  <Controller
                    name="recordingTime"
                    control={control}
                    render={({ field: { onChange, value } }) => (
                      <DatePicker
                        label="Date"
                        timezone="UTC"
                        disabled={readOnly}
                        value={value}
                        onChange={onChange}
                        slotProps={{
                          textField: {
                            fullWidth: true,
                          },
                        }}
                        sx={{
                          "& .MuiPickersSectionList-root.MuiPickersInputBase-sectionsContainer":
                            {
                              width: 1,
                            },
                        }}
                      />
                    )}
                  />
                </LocalizationProvider>
              </Grid>
              <Grid size={{ xs: 12, sm: 6, md: 3, lg: 1.75, xl: 1.5 }}>
                <LocalizationProvider dateAdapter={AdapterDayjs}>
                  <Controller
                    name="recordingTime"
                    control={control}
                    render={({ field: { onChange, value } }) => (
                      <TimePicker
                        label="Time"
                        timezone="UTC"
                        disabled={readOnly}
                        value={value}
                        onChange={onChange}
                        slotProps={{
                          textField: {
                            fullWidth: true,
                          },
                        }}
                        sx={{
                          "& .MuiPickersSectionList-root.MuiPickersInputBase-sectionsContainer":
                            {
                              width: 1,
                            },
                        }}
                      />
                    )}
                  />
                </LocalizationProvider>
              </Grid>
            </Grid>
            <Grid container spacing={2} mt={2}>
              <Grid size={{ xs: 12, sm: 6 }}>
                <Box mb={1}>
                  <Typography variant="caption">IMPORT</Typography>
                </Box>
                <FileDropzone
                  filesAdded={(fileList, jsonData) =>
                    addFiles(fileList, jsonData)
                  }
                  fileRemoved={(f) => uploadQueueRemoveFile(f)}
                  fileList={files}
                />
              </Grid>
              <Grid size={{ xs: 12, sm: 6 }}>
                <Box mb={1}>
                  <Typography variant="caption">IMPORT DETAILS</Typography>
                </Box>
                <Stack>
                  <FileRenderer fileData={fileData} />
                </Stack>
              </Grid>
            </Grid>
          </Stack>
          <Paper
            elevation={4}
            sx={{
              display: "flex",
              alignItems: "center",
              justifyContent: "space-between",
              p: 1,
              position: "fixed",
              transform: "translateX(-50%)",
              bottom: 72,
              left: "50%",
              zIndex: 1,
              width: "calc(100% - 1rem)",
            }}
          >
            <Button variant="outlined" onClick={handleReset}>
              Cancel
            </Button>
            <Button
              variant="contained"
              disabled={disableImport}
              endIcon={<ChevronRight />}
              type="submit"
            >
              Import Audio
            </Button>
          </Paper>
          <Box position="fixed" bottom={0} width={1} zIndex={1}>
            <ContentTitleBar
              name={af_case.caseName}
              label={readOnly ? "Select Files to Import" : "Create a New Clip"}
              onBack={() =>
                navigate(`/app/cases/${caseId}/clips`, { replace: true })
              }
            />
          </Box>
          <ClipImportWarningDialog
            handleSubmit={handleSubmit}
            warningMessage={warningMessage}
          />
        </Box>
      </FormProvider>
      <ImportErrorSnackbar />
    </Stack>
  );
}
